// iter-318b: Google Sign-Up — picks a role and finalises the new account.
// Reached only via AuthCallback after the backend returns 409 ACCOUNT_NOT_FOUND.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, PenTool, Sparkles, ClipboardCheck, Hammer, ArrowRight, Check } from 'lucide-react';
import { API } from '@/App';

const ROLES = [
  {
    id: 'client',
    title: 'Client / Property Owner',
    blurb: 'Track BOQs, milestones, and payments for your home.',
    Icon: Home,
    accent: 'from-amber-50 to-orange-50 border-amber-300 hover:border-amber-500',
    selectedRing: 'ring-amber-500',
  },
  {
    id: 'architect',
    title: 'Architect',
    blurb: 'Manage drawings, BOQ approvals, vendor scope side-by-side.',
    Icon: PenTool,
    accent: 'from-slate-50 to-stone-50 border-stone-300 hover:border-stone-600',
    selectedRing: 'ring-stone-700',
  },
  {
    id: 'interior_designer',
    title: 'Interior Designer',
    blurb: 'Spec finishes, render moodboards, ship branded estimates.',
    Icon: Sparkles,
    accent: 'from-rose-50 to-pink-50 border-rose-300 hover:border-rose-500',
    selectedRing: 'ring-rose-500',
  },
  {
    id: 'consultant',
    title: 'Consultant',
    blurb: 'Review BOQs, leave comments, approve vendor scope.',
    Icon: ClipboardCheck,
    accent: 'from-blue-50 to-indigo-50 border-blue-300 hover:border-blue-500',
    selectedRing: 'ring-blue-500',
  },
  {
    id: 'vendor',
    title: 'Vendor / Contractor',
    blurb: 'Receive scope, submit quotes, raise running bills.',
    Icon: Hammer,
    accent: 'from-emerald-50 to-teal-50 border-emerald-300 hover:border-emerald-500',
    selectedRing: 'ring-emerald-500',
  },
];

export default function GoogleSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const [selectedRole, setSelectedRole] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Guard: must have google_token from AuthCallback. Otherwise bounce.
    if (!state.google_token) {
      toast.error('Sign-up session not found. Please start again.');
      navigate('/login', { replace: true });
    }
  }, [state.google_token, navigate]);

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error('Please pick a role to continue');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/google-signup`, {
        google_token: state.google_token,
        role: selectedRole,
        phone,
        organization,
      });
      const { token, user } = res.data || {};
      if (!token || !user) throw new Error('Invalid response from server');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      toast.success(`Welcome aboard, ${user.name || user.email}`);
      navigate('/', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Sign-up failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!state.google_token) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/40 to-stone-100 py-10 px-4" data-testid="google-signup-page">
      <div className="max-w-3xl mx-auto">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <span className="text-stone-800 font-medium tracking-wide">BELLA CASA</span>
        </div>

        {/* Google profile card */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-6 mb-6 flex items-center gap-4">
          {state.picture ? (
            <img src={state.picture} alt={state.name} className="w-14 h-14 rounded-full" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 font-semibold text-lg">
              {(state.name || state.email || 'U').slice(0,1).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <p className="text-stone-900 font-semibold" data-testid="google-signup-name">{state.name || state.email}</p>
            <p className="text-stone-500 text-sm" data-testid="google-signup-email">{state.email}</p>
          </div>
          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Verified by Google</span>
        </div>

        {/* Header */}
        <div className="mb-5">
          <h1 className="font-playfair text-3xl font-bold text-stone-900 mb-2">Almost there — what brings you here?</h1>
          <p className="text-stone-500">Pick the option that best describes you. You can refine it later in Settings.</p>
        </div>

        {/* Role cards */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {ROLES.map((r) => {
            const isSelected = selectedRole === r.id;
            const Icon = r.Icon;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelectedRole(r.id)}
                className={`relative text-left p-4 rounded-xl bg-gradient-to-br ${r.accent} border-2 transition-all duration-150 ${isSelected ? `ring-2 ${r.selectedRing} ring-offset-2 shadow-lg` : 'shadow-sm'}`}
                data-testid={`role-card-${r.id}`}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-stone-900 text-white rounded-full flex items-center justify-center">
                    <Check size={14} />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/80 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-stone-800" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-stone-900 leading-tight">{r.title}</p>
                    <p className="text-xs text-stone-600 mt-1 leading-snug">{r.blurb}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Optional fields */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-5 mb-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Phone <span className="text-stone-400 font-normal">(optional)</span></label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="+91 9XXXXXXXXX"
                data-testid="google-signup-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Organization <span className="text-stone-400 font-normal">(optional)</span></label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Studio name, firm, etc."
                data-testid="google-signup-organization"
              />
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          disabled={!selectedRole || submitting}
          onClick={handleSubmit}
          className="w-full py-3.5 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group shadow-md"
          data-testid="google-signup-submit"
        >
          {submitting ? (
            <span>Creating your account...</span>
          ) : (
            <>
              <span>Create my account</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>

        <p className="text-center text-xs text-stone-400 mt-4">
          By continuing, you agree to our <a href="/legal/terms" className="underline hover:text-stone-600">Terms</a> and <a href="/legal/privacy" className="underline hover:text-stone-600">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
